// ─────────────────────────────────────────────────────────────────
// Glass-i — Production Department Master page
// (Stage 6B.8A-3K.3 + 3K.6 execution-order extension).
//
// Compact spreadsheet-style table — same density as Process Master.
// No bulky Tags; type / work-mode / active rendered as slim inline
// pills. ~28 px rows, slate-50 header, light blue hover, ▲▼✎×
// borderless actions.
//
// Stage 6B.8A-3K.6 adds:
//   • Execution Order / Group / Route Stage / Parallel? columns
//   • Hierarchy ↔ Execution Flow view toggle in the toolbar
//   • Sort defaults to executionOrder NULLS LAST → sequenceNo → code
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Input, Select, Space, Spin, Table, Tag, Tooltip, Modal, message
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  ArrowUpOutlined, ArrowDownOutlined, ReloadOutlined,
  BranchesOutlined, SearchOutlined, FilterOutlined,
  TableOutlined, NodeIndexOutlined
} from '@ant-design/icons';

import useApprovalMfa from '../../../../hooks/useApprovalMfa';
import { productionDepartmentsApi } from './productionDepartmentsApi';
import {
  buildDepartmentTree, flattenDepartmentTree,
  workModeLabel, departmentTypeLabel, executionGroupLabel,
  buildDepartmentPayload, summariseRemovalImpact, EXECUTION_GROUPS
} from './productionDepartmentsHelpers';
import ProductionDepartmentDrawer from './ProductionDepartmentDrawer';
import ExecutionFlowView from './ExecutionFlowView';
import './ProductionDepartmentsPage.css';

const TYPE_CLASS = {
  main:    'pd-pill pd-pill--main',
  sub:     'pd-pill pd-pill--sub',
  qc:      'pd-pill pd-pill--qc',
  support: 'pd-pill pd-pill--support'
};
const MODE_CLASS = {
  manual:         'pd-pill pd-pill--manual',
  machine:        'pd-pill pd-pill--machine',
  mixed:          'pd-pill pd-pill--mixed',
  outsourced:     'pd-pill pd-pill--outsourced',
  not_applicable: 'pd-pill pd-pill--na'
};

const TYPE_OPTIONS = [
  { value: 'main', label: 'Main' },
  { value: 'sub',  label: 'Sub' },
  { value: 'qc',   label: 'QC' },
  { value: 'support', label: 'Support' }
];
const MODE_OPTIONS = [
  { value: 'manual',         label: 'Manual' },
  { value: 'machine',        label: 'Machine' },
  { value: 'mixed',          label: 'Mixed' },
  { value: 'outsourced',     label: 'Outsourced' },
  { value: 'not_applicable', label: 'Not Applicable' }
];

export default function ProductionDepartmentsPage() {
  const [rows,     setRows]     = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');
  const [typeF,    setTypeF]    = useState(undefined);
  const [modeF,    setModeF]    = useState(undefined);
  const [groupF,   setGroupF]   = useState(undefined);
  const [view,     setView]     = useState('hierarchy'); // 'hierarchy' | 'flow'
  const [drawer,   setDrawer]   = useState({ open: false, mode: 'create',
                                              initial: null });
  const [submitting, setSubmitting] = useState(false);
  const [flowRows, setFlowRows] = useState(null);
  const [flowLoading, setFlowLoading] = useState(false);
  const [flowSaving, setFlowSaving] = useState(false);

  // WO-2C — Approval-MFA step-up for the two route-destructive actions
  // (Route-Stage/Active OFF, and archive).
  const { runWithApproval, ApprovalMfaModalSlot } = useApprovalMfa();

  // Show the hard-block dialog when a disable/archive would strand active
  // work orders or orphan a glass type. Returns true when blocked (caller
  // should stop). `impact` is the /removal-impact payload.
  const showRemovalBlock = useCallback((impact, verb) => {
    const { blocked, reasons } = summariseRemovalImpact(impact);
    if (!blocked) return false;
    Modal.error({
      title: `Cannot ${verb} “${impact.department?.departmentName || 'this department'}”`,
      content: (
        <div style={{ fontSize: 13 }}>
          {reasons.map((p, i) => <p key={i} style={{ margin: '6px 0' }}>{p}</p>)}
        </div>
      ),
      okText: 'Got it',
      width: 460
    });
    return true;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await productionDepartmentsApi.list({ limit: 1000 });
      setRows((out && out.rows) || []);
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || e.message || 'Failed to load departments.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Load (or refresh) execution-flow only when that view is selected.
  // Keeps the page snappy in hierarchy view + means a fresh save
  // re-fetches the flow next time it's opened.
  const loadFlow = useCallback(async () => {
    setFlowLoading(true);
    try {
      const out = await productionDepartmentsApi.executionFlow();
      setFlowRows((out && out.rows) || []);
    } catch (e) {
      // Soft-fail — ExecutionFlowView falls back to DEFAULT_EXECUTION_FLOW.
      setFlowRows([]);
    } finally {
      setFlowLoading(false);
    }
  }, []);

  // Save handler for ExecutionFlowView — receives a list of
  // `{ id, executionOrder }` updates (one entry per affected
  // department, including alternatives that share a parent's order)
  // and posts them through the existing /reorder endpoint. Reloads
  // both views after save so the hierarchy table and the flow stay
  // in sync.
  const saveFlowOrder = useCallback(async (updates) => {
    if (!Array.isArray(updates) || updates.length === 0) return;
    setFlowSaving(true);
    try {
      await productionDepartmentsApi.reorder({ orders: updates });
      message.success(`Updated execution order for ${updates.length} departments.`);
      // Refresh both the hierarchy + the flow so the new order
      // propagates everywhere on the page.
      await Promise.all([load(), loadFlow()]);
    } finally {
      setFlowSaving(false);
    }
  }, [load, loadFlow]);

  useEffect(() => {
    if (view === 'flow' && flowRows === null) loadFlow();
  }, [view, flowRows, loadFlow]);

  const mainRows = useMemo(
    () => rows.filter((r) => !r.parentDepartmentId && r.departmentType !== 'sub'),
    [rows]);

  const flat = useMemo(() => {
    const tree = buildDepartmentTree(rows);
    return flattenDepartmentTree(tree);
  }, [rows]);

  const visible = useMemo(() => {
    const needle = (search || '').trim().toLowerCase();
    return flat.filter((r) => {
      if (typeF && r.departmentType !== typeF) return false;
      if (modeF && r.workMode !== modeF) return false;
      if (groupF && r.executionGroup !== groupF) return false;
      if (!needle) return true;
      return [r.departmentName, r.departmentCode, r.abbreviation]
        .some((v) => v && String(v).toLowerCase().includes(needle));
    });
  }, [flat, search, typeF, modeF, groupF]);

  const submit = async (values) => {
    setSubmitting(true);
    const payload = buildDepartmentPayload(values);
    const id = drawer.initial && drawer.initial.id;
    const isEdit = drawer.mode === 'edit' && id;
    // WO-2C — a PUT that turns Route-Stage or Active OFF removes a live
    // stage: pre-check impact, hard-block if unsafe, else step up via MFA.
    const removingStage = isEdit
      && (payload.isRouteStage === false || payload.isActive === false);
    try {
      if (!isEdit) {
        await productionDepartmentsApi.create(payload);
        message.success('Department added.');
      } else if (removingStage) {
        const impact = await productionDepartmentsApi.removalImpact(id);
        if (showRemovalBlock(impact, 'disable')) { setSubmitting(false); return; }
        await runWithApproval({
          operationKey: 'production_department.deactivate',
          resourceType: 'production_department',
          resourceId:   id,
          operationLabel: 'Disable production stage',
          resourceLabel:  drawer.initial.departmentName,
          action: (cid) => productionDepartmentsApi.update(id, payload, cid)
        });
        message.success('Department updated.');
      } else {
        await productionDepartmentsApi.update(id, payload);
        message.success('Department updated.');
      }
      setDrawer({ open: false, mode: 'create', initial: null });
      load();
      // Invalidate the cached flow so the next switch to flow view
      // re-fetches with the latest data.
      setFlowRows(null);
    } catch (e) {
      if (e && e.name === 'ApprovalMfaCancelled') { setSubmitting(false); return; }
      message.error(e?.response?.data?.error?.message
        || 'Failed to save department.');
    } finally {
      setSubmitting(false);
    }
  };

  const openAddMain = () => setDrawer({ open: true, mode: 'create',
    initial: { departmentType: 'main', workMode: null, sequenceNo: 10,
               isRouteStage: true, allowParallelExecution: false } });
  const openAddSub = () => {
    if (!mainRows.length) {
      message.warning('Add a Main Department first.');
      return;
    }
    setDrawer({ open: true, mode: 'create',
      initial: { departmentType: 'sub', workMode: 'manual',
                 parentDepartmentId: mainRows[0].id, sequenceNo: 1,
                 isRouteStage: true, allowParallelExecution: false } });
  };
  const openEdit = (row) => setDrawer({ open: true, mode: 'edit',
    initial: { ...row } });

  // WO-2C — archive: pre-check impact (hard-block if unsafe), then step up
  // via MFA before soft-deleting. Replaces the plain Popconfirm.
  const onArchive = async (row) => {
    try {
      const impact = await productionDepartmentsApi.removalImpact(row.id);
      if (showRemovalBlock(impact, 'archive')) return;
      await runWithApproval({
        operationKey: 'production_department.delete',
        resourceType: 'production_department',
        resourceId:   row.id,
        operationLabel: 'Archive department',
        resourceLabel:  row.departmentName,
        action: (cid) => productionDepartmentsApi.remove(row.id, cid)
      });
      message.success(`Archived ${row.departmentName}.`);
      load();
      setFlowRows(null);
    } catch (e) {
      if (e && e.name === 'ApprovalMfaCancelled') return;
      message.error(e?.response?.data?.error?.message || 'Failed to archive.');
    }
  };

  const move = async (row, direction) => {
    const peers = visible.filter((r) =>
      r.depth === row.depth
      && (r.parentDepartmentId || null) === (row.parentDepartmentId || null));
    const idx = peers.findIndex((r) => r.id === row.id);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= peers.length) return;
    const other = peers[swapIdx];
    try {
      await productionDepartmentsApi.reorder({
        orders: [
          { id: row.id,   sequenceNo: other.sequenceNo },
          { id: other.id, sequenceNo: row.sequenceNo }
        ]
      });
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to reorder.');
    }
  };

  // ── Columns (compact, no Tags) ──────────────────────────────
  const columns = [
    { title: '#', key: '__sno', width: 38, align: 'center',
      render: (_v, _r, i) => <span style={{ color: '#94a3b8' }}>{i + 1}</span> },
    { title: 'Department', dataIndex: 'departmentName', width: 240,
      render: (v, row) => (
        <span className={`pd-name ${row.depth === 0 ? 'pd-name--main' : 'pd-name--sub'}`}
              style={{ paddingLeft: row.depth ? row.depth * 16 : 0 }}>
          {row.depth > 0 && <span className="pd-name__caret">└</span>}
          <span>{v}</span>
        </span>
      ) },
    { title: 'Code', dataIndex: 'departmentCode', width: 120,
      render: (v) => v
        ? <span className="pd-code">{v}</span>
        : <span className="pd-empty-cell">—</span> },
    { title: 'Abbr', dataIndex: 'abbreviation', width: 64,
      render: (v) => v
        ? <span className="pd-code" style={{ color: '#475569' }}>{v}</span>
        : <span className="pd-empty-cell">—</span> },
    { title: 'Type', dataIndex: 'departmentType', width: 72,
      render: (v) => (
        <span className={TYPE_CLASS[v] || 'pd-pill pd-pill--sub'}>
          {departmentTypeLabel(v)}
        </span>
      ) },
    { title: 'Work Mode', dataIndex: 'workMode', width: 100,
      render: (v) => v
        ? <span className={MODE_CLASS[v] || 'pd-pill pd-pill--na'}>
            {workModeLabel(v)}
          </span>
        : <span className="pd-empty-cell">—</span> },
    // ── 6B.8A-3K.6 ──
    { title: 'Exec #', dataIndex: 'executionOrder', width: 64, align: 'right',
      render: (v) => (v == null
        ? <span className="pd-empty-cell">—</span>
        : <span className="pd-num" style={{ fontWeight: 600,
            color: '#1d4ed8' }}>{v}</span>) },
    { title: 'Exec Group', dataIndex: 'executionGroup', width: 130,
      render: (v) => v
        ? <Tag style={{ margin: 0, fontSize: 10.5, lineHeight: '17px',
            padding: '0 6px', height: 18, borderRadius: 3,
            background: '#f1f5f9', color: '#475569',
            border: '1px solid #e2e8f0' }}>
            {executionGroupLabel(v) || v}
          </Tag>
        : <span className="pd-empty-cell">—</span> },
    { title: 'Route?', dataIndex: 'isRouteStage', width: 70, align: 'center',
      render: (v) => (
        <span className={`pd-pill ${v ? 'pd-pill--active' : 'pd-pill--inactive'}`}>
          {v ? 'On' : 'Off'}
        </span>
      ) },
    { title: 'Parallel?', dataIndex: 'allowParallelExecution', width: 80,
      align: 'center',
      render: (v) => v
        ? <span className="pd-pill pd-pill--mixed">Yes</span>
        : <span className="pd-empty-cell">—</span> },
    { title: 'Seq', dataIndex: 'sequenceNo', width: 56, align: 'right',
      render: (v) => <span className="pd-num">{v}</span> },
    { title: 'Active', dataIndex: 'isActive', width: 78, align: 'center',
      render: (v) => (
        <span className={`pd-pill ${v ? 'pd-pill--active' : 'pd-pill--inactive'}`}>
          {v ? 'Active' : 'Inactive'}
        </span>
      ) },
    { title: 'Actions', key: '__actions', width: 132, align: 'right',
      fixed: 'right', className: 'pd-actions',
      render: (_v, row) => (
        <Space size={0}>
          <Tooltip title="Move up"><Button size="small" type="text"
            icon={<ArrowUpOutlined />}
            onClick={() => move(row, 'up')} /></Tooltip>
          <Tooltip title="Move down"><Button size="small" type="text"
            icon={<ArrowDownOutlined />}
            onClick={() => move(row, 'down')} /></Tooltip>
          <Tooltip title="Edit"><Button size="small" type="text"
            icon={<EditOutlined />} onClick={() => openEdit(row)} /></Tooltip>
          <Tooltip title="Archive"><Button size="small" type="text"
            danger icon={<DeleteOutlined />}
            onClick={() => onArchive(row)} /></Tooltip>
        </Space>
      ) }
  ];

  const mainCount = rows.filter((r) => !r.parentDepartmentId).length;
  const subCount  = rows.filter((r) =>  r.parentDepartmentId).length;

  return (
    <div className="pd-shell">
      {/* Header */}
      <div className="pd-header">
        <div>
          <div className="pd-header__title">
            <span className="pd-header__icon"><BranchesOutlined /></span>
            Production Departments
          </div>
          <div className="pd-header__subtitle">
            Reusable Main → Manual / Machine hierarchy. Consumed by Process Master
            and (future) Work Order, Production, Barcode and Costing.
          </div>
        </div>
        <Space size={6}>
          <span className="pd-stat-chip"><strong>{mainCount}</strong> main</span>
          <span className="pd-stat-chip"><strong>{subCount}</strong> sub</span>
          <span className="pd-stat-chip"><strong>{rows.length}</strong> total</span>
        </Space>
      </div>

      {/* Toolbar */}
      <div className="pd-toolbar">
        {/* Hierarchy / Flow toggle */}
        <div className="pd-view-toggle" role="tablist" aria-label="View">
          <Button size="small" type="text"
            className={view === 'hierarchy' ? 'is-active' : ''}
            icon={<TableOutlined />}
            onClick={() => setView('hierarchy')}>
            Hierarchy
          </Button>
          <Button size="small" type="text"
            className={view === 'flow' ? 'is-active' : ''}
            icon={<NodeIndexOutlined />}
            onClick={() => setView('flow')}>
            Execution Flow
          </Button>
        </div>

        {view === 'hierarchy' && (
          <>
            <Input allowClear size="small"
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              placeholder="Search name, code, abbr…"
              value={search} onChange={(e) => setSearch(e.target.value)}
              style={{ width: 220 }} />
            <Select size="small" allowClear placeholder="All types"
              suffixIcon={<FilterOutlined style={{ color: '#94a3b8' }} />}
              style={{ width: 120 }} value={typeF} onChange={setTypeF}
              options={TYPE_OPTIONS} />
            <Select size="small" allowClear placeholder="All work modes"
              suffixIcon={<FilterOutlined style={{ color: '#94a3b8' }} />}
              style={{ width: 150 }} value={modeF} onChange={setModeF}
              options={MODE_OPTIONS} />
            <Select size="small" allowClear placeholder="All exec groups"
              suffixIcon={<FilterOutlined style={{ color: '#94a3b8' }} />}
              style={{ width: 170 }} value={groupF} onChange={setGroupF}
              options={EXECUTION_GROUPS} />

            <div className="pd-toolbar__divider" />
            <span style={{ color: '#94a3b8', fontSize: 11.5 }}>
              {visible.length} shown
            </span>
          </>
        )}

        <div className="pd-toolbar__spacer" />

        <Tooltip title="Reload">
          <Button size="small" icon={<ReloadOutlined />}
            onClick={() => { load(); if (view === 'flow') loadFlow(); }} />
        </Tooltip>
        <Button size="small" icon={<PlusOutlined />} onClick={openAddSub}>
          Add Sub
        </Button>
        <Button size="small" type="primary" icon={<PlusOutlined />}
          onClick={openAddMain}>
          Add Main
        </Button>
      </div>

      {/* Body — switches between Hierarchy table and Execution Flow */}
      {view === 'hierarchy' ? (
        <div className="pd-card">
          <Spin spinning={loading} wrapperClassName="pd-spin">
            <Table
              className="pd-grid"
              size="small"
              rowKey="id"
              dataSource={visible}
              columns={columns}
              pagination={false}
              sticky
              scroll={{ x: 'max-content' }}
              rowClassName={(r) => r.isActive ? '' : 'pd-inactive-row'}
              locale={{
                emptyText: rows.length === 0
                  ? 'No departments yet. Click Add Main to start.'
                  : 'No departments match the current filter.'
              }}
            />
          </Spin>
        </div>
      ) : (
        <Spin spinning={flowLoading} wrapperClassName="pd-spin">
          <ExecutionFlowView
            rows={flowRows}
            onReorder={saveFlowOrder}
            saving={flowSaving} />
        </Spin>
      )}

      <ProductionDepartmentDrawer
        open={drawer.open}
        mode={drawer.mode}
        initialValues={drawer.initial}
        mainOptions={mainRows}
        submitting={submitting}
        onClose={() => setDrawer({ open: false, mode: 'create', initial: null })}
        onSubmit={submit}
      />

      {/* WO-2C — Approval-MFA step-up modal for disable/archive. */}
      {ApprovalMfaModalSlot}
    </div>
  );
}
